import React, { Children, useContext, createContext } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { borderColorPrimary, accentColorPrimary, fontSize14, backgroundColorLevelCurrent, grey3, grey60, setAppearanceConstantLocal } from "@churchofjesuschrist/eden-style-constants";
import DensityContext from "../Density/Context";
import Col from "../Col";
import ColGroup from "../ColGroup";
import ColumnHidingContext from "../ColumnHiding/Context";
import SelfSelectContext from "../SelfSelect/Context";
import THead from "../THead";
import RowSelectionContext from "../RowSelection/Context";
import CardViewContext from "../CardView/Context";
import SortingContext from "../Sorting/Context";
import StickyHeaderContext from "./StickyHeaderContext";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const [stripedBackgroundColorDef, stripedBackgroundColor] = setAppearanceConstantLocal("striped-background-color", {
  light: grey3,
  dark: grey60,
  prefix: "eden"
});

// Returns an array of objects representing the columns for the given
// tableChildren. Since the columns are composed as <ColGroup> and <Col>
// children of the <Table>, the responsibility to get this column data belongs
// here in the Table component.
//
// This can accept a list of "decorators" which can apply additional transforms
// to the column definitions.
function getColumns(tableChildren, _ref) {
  let [columnHidingDecorator, sortingDecorator] = _ref;
  // Will hold the column definition objects:
  const columns = [];

  // Loop through the <Col>s. Although the HTML spec says the <colgroup> start
  // tags are not required in certain conditions, the React HTML validator says
  // <col>s cannot appear as children of <table>s so we don't need a condition
  // to check for <Col> table children.
  Children.forEach(tableChildren, colGroup => {
    if (colGroup && colGroup.type === ColGroup) {
      if (colGroup.props.children) {
        Children.forEach(colGroup.props.children, col => {
          if ((col === null || col === void 0 ? void 0 : col.type) === Col) {
            let column = {
              id: col.props.id
            };
            column = columnHidingDecorator(column);
            column = sortingDecorator(column, colGroup, col);
            columns.push(column);
          }
        });
      } else {
        let column = {
          id: colGroup.props.id
        };
        column = columnHidingDecorator(column);
        column = sortingDecorator(column, colGroup);
        columns.push(column);
      }
    }

    // Now add the respective column headers from the thead's last tr:
    const tHead = Children.toArray(tableChildren).find(child => child.type === THead);
    if (tHead) {
      const tHeadLastRow = Children.toArray(tHead.props.children).at(-1);
      if (tHeadLastRow) {
        const columnHeaders = Children.toArray(tHeadLastRow.props.children).filter(header => typeof header === "object");
        columns.forEach((column, index) => {
          column.header = columnHeaders[index].props.children;
        });
      }
    }
  });
  return columns;
}
const border = `1px solid ${borderColorPrimary}`;
const hiddenColumnBorder = `2px solid ${accentColorPrimary}`;

// We have to use border-collapse:collapse to make the hidden columns inline border a solid line.
const Root = styled.table.withConfig({
  componentId: "sc-vxdosv-0"
})(["", " width:100%;border-collapse:collapse;border-block-start:0;--eden-table-density-divisor:", ";colgroup[hidden] + colgroup,col[hidden] + col{border-inline-start:", ";}colgroup:has(+ colgroup[hidden]),colgroup:has(col:last-child[hidden]){border-inline-end:", ";}td{line-height:1.25rem;}th:not(:first-child),td:not(:first-child){border-inline-start:", ";}tbody tr{border-block-start:", ";}[hidden] th,[hidden] td{border-inline-start:", ";}thead tr:last-of-type th,thead tr:last-of-type td{box-shadow:inset 0px -1px 0px 0px ", ";", "}tbody{font-size:", ";}tbody:first-of-type tr:first-of-type{border-block-start:0;}", ""], stripedBackgroundColorDef, _ref2 => {
  let {
    density
  } = _ref2;
  return density === "condensed" ? "2" : "1";
}, hiddenColumnBorder, hiddenColumnBorder, _ref3 => {
  let {
    dividers
  } = _ref3;
  return dividers === "full" ? border : "0";
}, _ref4 => {
  let {
    dividers
  } = _ref4;
  return dividers === "open" ? "0" : border;
}, hiddenColumnBorder, borderColorPrimary, _ref5 => {
  let {
    stickyHeader
  } = _ref5;
  return stickyHeader ? `position: sticky; top: 0; background: ${backgroundColorLevelCurrent}; background-clip: padding-box;` : "";
}, fontSize14, _ref6 => {
  let {
    striped
  } = _ref6;
  return striped ? `tbody tr:nth-child(odd) { background-color: ${stripedBackgroundColor()}; }` : "";
});
export const TableContext = /*#__PURE__*/createContext(children => children);
function decorateCell(originalCell) {
  let decorators = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  let options = arguments.length > 2 ? arguments[2] : undefined;
  return decorators.reduce((decoratedCell, decorator) => {
    return typeof decorator === "function" ? decorator(decoratedCell, options) : decoratedCell;
  }, originalCell);
}

/** Represents data with more than one dimension, in the form of a table. */
export default function Table(_ref7) {
  let {
    children,
    dividers = "full",
    striped,
    role = "grid",
    density,
    stickyHeader,
    ...props
  } = _ref7;
  const densityContext = useContext(DensityContext);
  const cardView = useContext(CardViewContext);
  const columnHiding = useContext(ColumnHidingContext);
  const {
    cellDecorator: sortingCellDecorator,
    columnDecorator: sortingColumnDecorator,
    ...sorting
  } = useContext(SortingContext);
  const columnsRef = useContext(SelfSelectContext);
  const rowSelection = useContext(RowSelectionContext);
  const stickyHeaderContext = useContext(StickyHeaderContext);

  // TODO: Is there a way to optimize/memoize this?
  // useMemo based on `children` runs every render.
  const columns = getColumns(children, [columnHiding.columnDecorator, sortingColumnDecorator]);
  if (columnsRef) {
    columnsRef.current = columns;
  }

  // Table provides a "renderRowChildren" function via a context to its <TR>
  // descendants. This applies a list of "decorators" which can apply
  // additional transforms to the row cells.
  //
  // This could go into a useCallback, but it depends on `columns` which
  // then depends on `children`, so its going to regenerate on each render
  // anyway :/
  function renderRowChildren(rowChildren) {
    return Children.toArray(rowChildren).map((cell, index) => {
      return decorateCell(cell, [cardView, sortingCellDecorator,
      // Needs to occur after sorting so we don't end up with buttons in buttons in the column headers
      columnHiding.cellDecorator], {
        rowChildren,
        index,
        columns,
        sorting
      });
    });
  }
  return /*#__PURE__*/_jsxs(Root, {
    ...props,
    dividers: dividers,
    striped: striped,
    density: densityContext || density,
    stickyHeader: stickyHeaderContext || stickyHeader,
    role: role,
    children: [rowSelection ? /*#__PURE__*/_jsx(rowSelection.RowSelectionColGroup, {}) : null, /*#__PURE__*/_jsx(TableContext.Provider, {
      value: renderRowChildren,
      children: /*#__PURE__*/_jsx(StickyHeaderContext.Provider, {
        value: stickyHeaderContext || stickyHeader,
        children: children
      })
    })]
  });
}
Table.propTypes = {
  /** The table contents. Must contain zero or more `ColGroup` elements, followed by an optional `THead` element, followed by zero or more `TBody` elements, followed by an optional `TFoot` element. */
  children: PropTypes.node,
  /** The density to apply to the table cells.
   *
   * If a `<SelfSelect>` ancestor is present, the table will default to the `<SelfSelect>`'s `density` value. */
  density: PropTypes.oneOf(["normal", "condensed"]),
  /** Which divider lines to apply. `full` shows both row and column dividers, `open` does not show row or column dividers, and `row` only shows row dividers. */
  dividers: PropTypes.oneOf(["full", "open", "row"]),
  /** The primary purpose of the table. `grid` is more for interactivity and `table` is more for reading. */
  role: PropTypes.oneOf(["grid", "table"]),
  /** Whether to make the last header row sticky.
   *
   * If a `<SelfSelect>` or `<OverflowWrapper>` ancestor is present, a `tableHeight` prop will need to be set there. */
  stickyHeader: PropTypes.bool,
  /** Whether to apply striping to body rows. */
  striped: PropTypes.bool
};